import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import {
  Alert,
  Button,
  Drawer,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import AppContext from "../../../context/app/context";
import AuthContext from "../../../context/auth/context";
import PageHeader from "../../../components/general/layout/page-header";
import EndPoints from "../../../services/end-points";
import { IProduct } from "../../../models/product/response";
import { BooleanStatus } from "../../../models/product/enum";
import { IStore } from "../../../models/store/response";
import {
  IDailyStockCount,
  IDailyStockRevision,
} from "../../../models/wms/daily-stock-count";
import { Permissions } from "../../../utils/Permissions/Permissions";
import {
  countDay,
  countTime,
  damascusNow,
  latestCounts,
} from "../../../utils/helpers/daily-stock-count";
import { createUuidV4 } from "../../../utils/helpers/uuid";
import styles from "./style.module.scss";

interface Draft {
  quantity: number | null;
  time: string;
  key: string;
  correction?: IDailyStockCount;
}

interface CorrectionDraft extends Draft {
  correction: IDailyStockCount;
}

const correctionDraft = (count: IDailyStockCount): CorrectionDraft => ({
  quantity: count.quantity,
  time: count.countedAt
    ? dayjs(count.countedAt).tz("Asia/Damascus").format("HH:mm:ss")
    : "",
  key: createUuidV4(),
  correction: count,
});

// Pagination requires a positive size; the virtual table only mounts visible rows.
const ALL_PRODUCTS_PAGE_SIZE = Number.MAX_SAFE_INTEGER;

const openPicker = (event: ReactMouseEvent<HTMLInputElement>) => {
  try {
    event.currentTarget.showPicker?.();
  } catch {
    // Keep native typing available if the browser blocks opening the picker.
  }
};

const DailyStockCountPage = () => {
  const { t, i18n } = useTranslation();
  const { direction } = useContext(AppContext);
  const { userPermissions } = useContext(AuthContext);
  const [params] = useSearchParams();
  const [storeId, setStoreId] = useState<number | undefined>(
    () => Number(params.get("storeId")) || undefined,
  );
  const [day, setDay] = useState(countDay);
  const [stores, setStores] = useState<IStore[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [counts, setCounts] = useState<IDailyStockCount[]>([]);
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [keyword, setKeyword] = useState("");
  const [singleProduct, setSingleProduct] = useState<number | undefined>(
    () => Number(params.get("productId")) || undefined,
  );
  const [status, setStatus] = useState("all");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [historyProductId, setHistoryProductId] = useState<number | null>(null);
  const [historyDraft, setHistoryDraft] = useState<CorrectionDraft | null>(
    null,
  );
  const [historyError, setHistoryError] = useState("");
  const [revision, setRevision] = useState<IDailyStockCount | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const busy = useRef(false);
  const canCreate = userPermissions.includes(
    Permissions.CAN_CREATE_DAILY_STOCK_COUNTS,
  );
  const canUpdate = userPermissions.includes(
    Permissions.CAN_UPDATE_DAILY_STOCK_COUNTS,
  );
  const latest = useMemo(() => latestCounts(counts), [counts]);
  const productsById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const countedProducts = useMemo(
    () => products.filter((product) => latest.has(product.id)).length,
    [products, latest],
  );
  const history = useMemo(
    () =>
      historyProductId === null
        ? null
        : counts.filter((count) => count.productId === historyProductId),
    [counts, historyProductId],
  );
  const historyProduct =
    historyProductId === null ? undefined : productsById.get(historyProductId);
  const dirtyCount = Object.values(drafts).filter(
    (draft) => draft.quantity !== null,
  ).length;
  const dirty = Object.keys(drafts).length > 0 || historyDraft !== null;
  const txt = useCallback((key: string) => t(`daily_count_${key}`), [t]);

  useEffect(() => {
    let active = true;
    setStoresLoading(true);
    EndPoints.wmsShared
      .getDropdownStores()
      .then(({ data }) => {
        if (active) setStores(data.data);
      })
      .catch(() => {
        if (active) setError(txt("load_error"));
      })
      .finally(() => {
        if (active) setStoresLoading(false);
      });
    return () => {
      active = false;
    };
  }, [i18n.language, txt]);

  useEffect(() => {
    if (!storeId) return;
    let active = true;
    setLoading(true);
    setError("");
    setProducts([]);
    setCounts([]);
    Promise.all([
      EndPoints.product.getAllProducts({
        light: true,
        perPage: -1,
        filters: [
          { name: "storeId", operation: "eq", value: storeId },
          { name: "status", operation: "eq", value: BooleanStatus.ACTIVE },
          { name: "storeStatus", operation: "eq", value: BooleanStatus.ACTIVE },
        ],
      }),
      EndPoints.dailyStockCount.list(storeId, day),
    ])
      .then(([productResponse, countResponse]) => {
        if (!active) return;
        setProducts(productResponse.data.data);
        setCounts(countResponse.data.data);
      })
      .catch(() => {
        if (active) setError(txt("load_error"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [storeId, day, i18n.language, txt, reload]);

  useEffect(() => {
    setPage(1);
  }, [storeId, day, keyword, status, singleProduct, pageSize]);

  useEffect(() => {
    if (!dirty) return;
    const leave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const navigate = (event: MouseEvent) => {
      const anchor = (event.target as Element)?.closest?.("a[href]");
      if (anchor && !window.confirm(txt("discard"))) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", leave);
    document.addEventListener("click", navigate, true);
    return () => {
      window.removeEventListener("beforeunload", leave);
      document.removeEventListener("click", navigate, true);
    };
  }, [dirty, txt]);

  const changeScope = (nextStore: number | undefined, nextDay: string) => {
    if (!nextDay || (dirty && !window.confirm(txt("discard")))) return;
    setDrafts({});
    setStoreId(nextStore);
    setDay(nextDay);
    setHistoryProductId(null);
    setHistoryDraft(null);
    setHistoryError("");
    setRevision(null);
  };
  const changeDraft = (id: number, patch: Partial<Draft>) =>
    setDrafts((previous) => ({
      ...previous,
      [id]: {
        quantity: null,
        time: damascusNow().format("HH:mm:ss"),
        ...previous[id],
        ...patch,
        key: createUuidV4(),
      },
    }));
  const removeDraft = (id: number) =>
    setDrafts((previous) => {
      const next = { ...previous };
      delete next[id];
      return next;
    });
  const beginCorrection = (count: IDailyStockCount) => {
    if (drafts[count.productId] && !window.confirm(txt("discard"))) return;
    setDrafts((previous) => ({
      ...previous,
      [count.productId]: correctionDraft(count),
    }));
  };
  const refreshCounts = async () => {
    const response = await EndPoints.dailyStockCount.list(storeId!, day);
    setCounts(response.data.data);
  };
  const isValidDraft = (productId: number, draft: Draft) => {
    if (draft.quantity === null) return false;
    const factor =
      draft.correction?.unitFactor ??
      productsById.get(productId)?.unit?.factorAccountingSystem ??
      1;
    const physical = draft.quantity * factor;
    const countedAt = countTime(day, draft.time);
    return (
      draft.quantity >= 0 &&
      Number.isFinite(physical) &&
      Math.abs(Math.round(physical) - physical) <= 0.00001 &&
      physical <= 1e12 &&
      (!countedAt || !dayjs(countedAt).isAfter(dayjs()))
    );
  };
  const saveHistoryCorrection = async () => {
    if (busy.current || !canUpdate || !historyDraft?.correction) return;
    const draft = historyDraft;
    if (!isValidDraft(draft.correction.productId, draft)) {
      setHistoryError(txt("invalid"));
      return;
    }
    busy.current = true;
    setSaving(true);
    setHistoryError("");
    try {
      const { data: corrected } = await EndPoints.dailyStockCount.correct(
        draft.correction.id,
        {
          quantity: draft.quantity!,
          countedAt: countTime(day, draft.time),
          expectedVersion: draft.correction.version,
        },
      );
      setCounts((previous) =>
        previous.map((count) =>
          count.id === corrected.id ? corrected : count,
        ),
      );
      setHistoryDraft(null);
      setRevision(null);
      message.success(txt("correction_saved"));
    } catch (failure: any) {
      setHistoryError(failure?.response?.data?.message || txt("save_error"));
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };
  const save = async () => {
    if (busy.current || !storeId || !dirtyCount) return;
    const entries = Object.entries(drafts).filter(
      ([, draft]) => draft.quantity !== null,
    );
    for (const [id, draft] of entries) {
      const product = productsById.get(Number(id));
      if (!isValidDraft(Number(id), draft)) {
        message.error(`${product?.name ?? id}: ${txt("invalid")}`);
        return;
      }
    }
    busy.current = true;
    setSaving(true);
    setError("");
    try {
      const newEntries = entries.filter(([, draft]) => !draft.correction);
      if (newEntries.length) {
        // Keep each chunk atomic and preserve its keys on a failed retry.
        for (let index = 0; index < newEntries.length; index += 500) {
          const chunk = newEntries.slice(index, index + 500);
          await EndPoints.dailyStockCount.record(
            storeId,
            day,
            chunk.map(([id, draft]) => ({
              productId: Number(id),
              quantity: draft.quantity!,
              countedAt: countTime(day, draft.time),
              clientKey: draft.key,
            })),
          );
          chunk.forEach(([id]) => removeDraft(Number(id)));
        }
      }
      for (const [id, draft] of entries.filter(
        ([, entry]) => entry.correction,
      )) {
        await EndPoints.dailyStockCount.correct(draft.correction!.id, {
          quantity: draft.quantity!,
          countedAt: countTime(day, draft.time),
          expectedVersion: draft.correction!.version,
        });
        removeDraft(Number(id));
      }
      message.success(txt("saved"));
    } catch (failure: any) {
      const serverMessage = failure?.response?.data?.message;
      setError(serverMessage || txt("save_error"));
    } finally {
      try {
        await refreshCounts();
      } catch {
        setError(txt("load_error"));
      }
      busy.current = false;
      setSaving(false);
    }
  };
  const visible = useMemo(
    () =>
      products.filter(
        (product) =>
          (!singleProduct || product.id === singleProduct) &&
          `${product.name} ${product.id}`
            .toLowerCase()
            .includes(keyword.toLowerCase()) &&
          (status === "all" ||
            (status === "counted"
              ? latest.has(product.id)
              : !latest.has(product.id))),
      ),
    [products, singleProduct, keyword, status, latest],
  );
  const virtual = Math.min(pageSize, visible.length) > 50;
  const localTime = (value: string | null) =>
    value
      ? dayjs(value).tz("Asia/Damascus").format("YYYY-MM-DD HH:mm:ss")
      : txt("unknown_time");
  const revisions = revision?.revisions ?? [];

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader title={txt("title")} />
      <Typography.Paragraph type="secondary">
        {txt("subtitle")}
      </Typography.Paragraph>
      <Alert
        type="info"
        showIcon
        message={txt("definition")}
        description={txt("day_note")}
      />
      <div className={styles.toolbar}>
        <label className={styles.field}>
          {t("store")}
          <Select
            aria-label={t("store")}
            value={storeId}
            loading={storesLoading}
            showSearch
            optionFilterProp="label"
            disabled={saving}
            placeholder={t("select_store")}
            options={stores.map((store) => ({
              value: store.id,
              label: store.name,
            }))}
            onChange={(id) => changeScope(id, day)}
          />
        </label>
        <label className={styles.field}>
          {txt("day")}
          <Input
            type="date"
            onClick={openPicker}
            aria-label={txt("day")}
            value={day}
            max={countDay()}
            disabled={saving}
            onChange={(event) => changeScope(storeId, event.target.value)}
          />
        </label>
        <div className={styles.summary}>
          <Typography.Text>
            {txt("counted")}: {countedProducts} / {products.length}
          </Typography.Text>
          <Typography.Text type="secondary">
            {txt("pending")}: {dirtyCount}
          </Typography.Text>
        </div>
        {(canCreate || canUpdate) && (
          <Button
            type="primary"
            loading={saving}
            disabled={!dirtyCount || loading || !storeId}
            onClick={save}
          >
            {txt("save")} ({dirtyCount})
          </Button>
        )}
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          className={styles.error}
          action={
            <Button
              disabled={saving || loading}
              onClick={() => setReload((value) => value + 1)}
            >
              {txt("retry")}
            </Button>
          }
        />
      )}
      {!storeId ? (
        <Typography.Paragraph className={styles.empty}>
          {txt("select_store")}
        </Typography.Paragraph>
      ) : (
        <>
          <Space wrap className={styles.filters}>
            <Input.Search
              aria-label={txt("search")}
              placeholder={txt("search")}
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              allowClear
            />
            <Select
              aria-label={txt("filter")}
              value={status}
              onChange={setStatus}
              options={[
                { value: "all", label: txt("all") },
                { value: "counted", label: txt("counted") },
                { value: "uncounted", label: txt("uncounted") },
              ]}
            />
            {singleProduct && (
              <Button onClick={() => setSingleProduct(undefined)}>
                {txt("show_all")}
              </Button>
            )}
          </Space>
          <Table<IProduct>
            rowKey="id"
            dataSource={visible}
            loading={loading}
            size="small"
            virtual={virtual}
            scroll={{ x: 980, ...(virtual ? { y: 480 } : {}) }}
            pagination={{
              current: page,
              pageSize,
              pageSizeOptions: [10, 20, 25, 50, 100, ALL_PRODUCTS_PAGE_SIZE],
              showSizeChanger: {
                "aria-label": txt("page_size"),
                options: [
                  ...[10, 20, 25, 50, 100].map((value) => ({
                    value,
                    label: `${value} ${txt("per_page")}`,
                  })),
                  { value: ALL_PRODUCTS_PAGE_SIZE, label: txt("page_all") },
                ],
              },
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
            locale={{ emptyText: txt("empty") }}
            columns={[
              {
                title: t("id"),
                dataIndex: "id",
                width: 75,
                responsive: ["md"],
              },
              {
                title: t("product"),
                width: 180,
                render: (_, product) => (
                  <div>
                    {product.name}
                    <div>
                      <Typography.Text type="secondary">
                        {drafts[product.id]?.correction?.unit ??
                          product.unit?.name ??
                          ""}
                      </Typography.Text>
                    </div>
                  </div>
                ),
              },
              {
                title: txt("quantity"),
                width: 160,
                render: (_, product) => {
                  const draft = drafts[product.id];
                  const enabled = draft?.correction ? canUpdate : canCreate;
                  return (
                    <Space direction="vertical" size={2}>
                      {draft?.correction && (
                        <Tag color="orange">{txt("correcting")}</Tag>
                      )}
                      <InputNumber
                        aria-label={`${txt("quantity")} ${product.name}`}
                        min={0}
                        value={draft?.quantity ?? null}
                        disabled={!enabled || saving || loading}
                        onChange={(value) =>
                          changeDraft(product.id, {
                            quantity: value === null ? null : Number(value),
                          })
                        }
                        placeholder={txt("blank")}
                      />
                    </Space>
                  );
                },
              },
              {
                title: txt("time"),
                width: 160,
                render: (_, product) => (
                  <Input
                    type="time"
                    onClick={openPicker}
                    step={1}
                    aria-label={`${txt("time")} ${product.name}`}
                    disabled={
                      saving ||
                      loading ||
                      !(drafts[product.id]?.correction ? canUpdate : canCreate)
                    }
                    value={
                      drafts[product.id]?.time ??
                      damascusNow().format("HH:mm:ss")
                    }
                    onChange={(event) =>
                      changeDraft(product.id, { time: event.target.value })
                    }
                  />
                ),
              },
              {
                title: txt("latest"),
                width: 170,
                render: (_, product) => {
                  const count = latest.get(product.id);
                  return count ? (
                    <>
                      <div>
                        {count.quantity} {count.unit}
                      </div>
                      <Typography.Text type="secondary">
                        {localTime(count.countedAt)}
                      </Typography.Text>
                    </>
                  ) : (
                    <Tag>{txt("uncounted")}</Tag>
                  );
                },
              },
              {
                title: txt("actions"),
                width: 190,
                render: (_, product) => (
                  <Space wrap>
                    {latest.has(product.id) && canUpdate && (
                      <Button
                        type="link"
                        size="small"
                        disabled={saving}
                        onClick={() => beginCorrection(latest.get(product.id)!)}
                      >
                        {txt("correct")}
                      </Button>
                    )}
                    {latest.has(product.id) && (
                      <Button
                        type="link"
                        size="small"
                        disabled={saving}
                        onClick={() => {
                          setHistoryProductId(product.id);
                          setHistoryDraft(null);
                          setHistoryError("");
                          setRevision(null);
                        }}
                      >
                        {txt("history")}
                      </Button>
                    )}
                    {drafts[product.id] && (
                      <Button
                        type="link"
                        size="small"
                        disabled={saving}
                        onClick={() => removeDraft(product.id)}
                      >
                        {t("cancel")}
                      </Button>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </>
      )}
      <Drawer
        title={`${txt("history")}: ${historyProduct?.name ?? ""}`}
        open={history !== null}
        onClose={() => {
          if (saving || (historyDraft && !window.confirm(txt("discard"))))
            return;
          setHistoryProductId(null);
          setHistoryDraft(null);
          setHistoryError("");
          setRevision(null);
        }}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
        width={960}
      >
        <Typography.Paragraph type="secondary">
          {t("store")}: {stores.find((store) => store.id === storeId)?.name} ·{" "}
          {txt("day")}: {day}
        </Typography.Paragraph>
        {historyDraft && (
          <Alert
            type="info"
            showIcon
            message={txt("correction_hint")}
            className={styles.error}
          />
        )}
        {historyError && (
          <Alert
            type="error"
            showIcon
            message={historyError}
            className={styles.error}
          />
        )}
        <Table<IDailyStockCount>
          rowKey="id"
          pagination={false}
          dataSource={history ?? []}
          scroll={{ x: 880 }}
          size="small"
          columns={[
            { title: t("id"), dataIndex: "id", width: 65 },
            {
              title: txt("quantity"),
              width: 160,
              render: (_, count) =>
                historyDraft?.correction?.id === count.id ? (
                  <Space direction="vertical" size={2}>
                    <Tag color="orange">
                      {txt("correcting")} #{count.id}
                    </Tag>
                    <InputNumber
                      aria-label={`${txt("quantity")} ${historyProduct?.name} #${count.id}`}
                      min={0}
                      value={historyDraft.quantity}
                      disabled={saving}
                      onChange={(value) =>
                        setHistoryDraft((draft) =>
                          draft
                            ? {
                                ...draft,
                                quantity: value === null ? null : Number(value),
                              }
                            : null,
                        )
                      }
                    />
                    <Typography.Text type="secondary">
                      {count.unit}
                    </Typography.Text>
                  </Space>
                ) : (
                  `${count.quantity} ${count.unit}`
                ),
            },
            {
              title: txt("time"),
              width: 170,
              render: (_, count) =>
                historyDraft?.correction?.id === count.id ? (
                  <Input
                    type="time"
                    step={1}
                    onClick={openPicker}
                    aria-label={`${txt("time")} ${historyProduct?.name} #${count.id}`}
                    value={historyDraft.time}
                    disabled={saving}
                    onChange={(event) => {
                      const time = event.target.value;
                      setHistoryDraft((draft) =>
                        draft ? { ...draft, time } : null,
                      );
                    }}
                  />
                ) : (
                  localTime(count.countedAt)
                ),
            },
            {
              title: txt("recorded"),
              render: (_, count) => localTime(count.recordedAt),
            },
            { title: txt("by"), render: (_, count) => count.createdBy ?? "—" },
            {
              title: txt("actions"),
              render: (_, count) => (
                <Space>
                  {historyDraft?.correction?.id === count.id ? (
                    <>
                      <Button
                        type="primary"
                        size="small"
                        loading={saving}
                        disabled={historyDraft.quantity === null}
                        onClick={saveHistoryCorrection}
                      >
                        {txt("save_correction")}
                      </Button>
                      <Button
                        size="small"
                        disabled={saving}
                        onClick={() => {
                          setHistoryDraft(null);
                          setHistoryError("");
                        }}
                      >
                        {t("cancel")}
                      </Button>
                    </>
                  ) : (
                    canUpdate && (
                      <Button
                        type="link"
                        disabled={saving || historyDraft !== null}
                        onClick={() => {
                          setHistoryDraft(correctionDraft(count));
                          setHistoryError("");
                        }}
                      >
                        {txt("correct")}
                      </Button>
                    )
                  )}
                  <Button
                    type="link"
                    disabled={historyLoading}
                    onClick={async () => {
                      setHistoryLoading(true);
                      try {
                        setRevision(
                          (await EndPoints.dailyStockCount.history(count.id))
                            .data,
                        );
                      } catch {
                        message.error(txt("load_error"));
                      } finally {
                        setHistoryLoading(false);
                      }
                    }}
                  >
                    {txt("revisions")}
                  </Button>
                </Space>
              ),
            },
          ]}
        />
        {revision && (
          <>
            <Typography.Title level={5}>
              {txt("revisions")} #{revision.id}
            </Typography.Title>
            <Table<IDailyStockRevision>
              rowKey="version"
              size="small"
              dataSource={revisions}
              pagination={false}
              columns={[
                { title: txt("version"), dataIndex: "version" },
                { title: txt("quantity"), dataIndex: "quantity" },
                {
                  title: txt("time"),
                  render: (_, value) => localTime(value.countedAt),
                },
                {
                  title: txt("recorded"),
                  render: (_, value) => localTime(value.recordedAt),
                },
                { title: txt("by"), dataIndex: "recordedBy" },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default DailyStockCountPage;
